import { Deferred, Head, router, usePage } from '@inertiajs/react'
import { CreditCardIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Button, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui'
import { TransactionStatusBadge } from '@/components/common/TransactionStatusBadge'
import { MonoId } from '@/components/common/MonoId'
import { EmptyState } from '@/components/common/EmptyState'
import { GatewayNotice } from '@/components/common/GatewayNotice'
import { Pagination } from '@/components/common/Pagination'
import { TableSkeleton } from '@/components/common/Skeletons'
import { ExportMenu } from '@/components/common/ExportMenu'
import { TransactionFilters, type FilterValues, type FilterOptions } from '@/components/common/TransactionFilters'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDateTime } from '@/lib/format'
import type { GatewayState, GatewayError, PageResult, PaymentRecord, SharedProps } from '@/types'

interface PaymentsIndexProps {
  payments?: { page: PageResult<PaymentRecord> | null; error: GatewayError | null }
  filters: FilterValues
  filter_options: FilterOptions
  per: number
  gateway: GatewayState
}

function PaymentsIndex({ payments, filters, filter_options, per, gateway }: PaymentsIndexProps) {
  const { t, locale } = useTranslations()
  const { props } = usePage<SharedProps>()
  const canManage = ['superadmin', 'admin'].includes(props.auth.user?.role ?? '')
  const manageHref = gateway.merchant ? `/merchants/${gateway.merchant.id}/edit` : undefined

  function navigate(nextFilters: FilterValues, nextPage = 1) {
    router.get('/payments', { filters: nextFilters, page: nextPage, per }, { preserveScroll: true, preserveState: true, replace: true })
  }

  return (
    <>
      <Head title={t('payments.title')} />
      <PageHeading
        title={t('payments.title')}
        actions={
          gateway.configured && (
            <>
              <Button outline onClick={() => router.reload({ only: ['payments'], data: { refresh: true } })}>
                {t('common.refresh')}
              </Button>
              <ExportMenu kind="payments" filters={filters} />
            </>
          )
        }
      />

      <div className="mt-6 flex flex-col gap-6">
        <GatewayNotice state={gateway} manageHref={canManage ? manageHref : undefined} createHref={canManage ? '/merchants' : undefined} />

        {gateway.configured && (
          <>
            <TransactionFilters filters={filters} options={filter_options} onApply={(f) => navigate(f)} />

            <Deferred data="payments" fallback={<TableSkeleton rows={6} cols={6} />}>
              {payments?.error ? (
                <GatewayNotice state={{ ...gateway, error: payments.error }} />
              ) : payments?.page && payments.page.items.length > 0 ? (
                <div className="flex flex-col gap-4">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>{t('payments.columns.date')}</TableHeader>
                        <TableHeader>{t('payments.columns.transaction')}</TableHeader>
                        <TableHeader>{t('payments.columns.status')}</TableHeader>
                        <TableHeader>{t('payments.columns.method')}</TableHeader>
                        <TableHeader className="text-right">{t('payments.columns.amount')}</TableHeader>
                        <TableHeader>{t('payments.columns.reference')}</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.page.items.map((p) => (
                        <TableRow key={p.transaction_id} href={`/payments/${p.transaction_id}`}>
                          <TableCell className="whitespace-nowrap">{formatDateTime(p.created_at, locale)}</TableCell>
                          <TableCell>
                            <MonoId value={p.transaction_id} />
                          </TableCell>
                          <TableCell>
                            <TransactionStatusBadge status={p.status} family={p.status_family} />
                          </TableCell>
                          <TableCell>{p.payment_method_name}</TableCell>
                          <TableCell className="text-right font-medium text-text-strong">
                            {formatMoney(p.amount, p.currency, locale)}
                          </TableCell>
                          <TableCell>
                            {p.order_id || p.merchant_reference ? (
                              <MonoId value={(p.order_id || p.merchant_reference) as string} />
                            ) : (
                              <span className="text-text/50">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination pagination={payments.page.pagination} onNavigate={(n) => navigate(filters, n)} />
                </div>
              ) : (
                <EmptyState
                  icon={CreditCardIcon}
                  title={t('payments.empty')}
                  action={
                    <Button outline onClick={() => navigate({})}>
                      {t('payments.filters.clear')}
                    </Button>
                  }
                />
              )}
            </Deferred>
          </>
        )}
      </div>
    </>
  )
}

PaymentsIndex.layout = withAppLayout

export default PaymentsIndex
